/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useState, useEffect } from 'react';
import FeesRadio from '../FeesRadio';
import ConfirmationDialog from './ConfirmationDialog';

/**
 * Wrapper of the <FeesRadio> that requires confirmation when changing from a specific radio value.
 *
 * Use example:
 *
 * The field that contains the fees radios should have the widget set in uiSchema.json:
 *     "ui:widget": "confirmedFeesRadioWidget",
 *
 * The same property definition should be updated with the popup objects within its config field in schema.jsonschema:
 *     "config": {
 *       "popup": {
 *         "enabled": true,
 *         "type": "warning",
 *         "titleText": "i18n(fees.payment.notIntendingToPay.popup.title)",
 *         "messageText": "i18n(fees.payment.notIntendingToPay.popup.msg)",
 *         "confirmButtonText": "i18n(fees.payment.notIntendingToPay.popup.confirm)",
 *         "cancelButtonText": "i18n(fees.payment.notIntendingToPay.popup.cancel)",
 *         "valueToConfirmChangeFrom": "alreadyPaid"
 *       }
 *     }
 *
 *     valueToConfirmChangeFrom: the value that requires a confirmation to move away from it
 */
const ConfirmedFeesRadioWidget = (props) => {
  const { schema, uiSchema, value, registry, id } = props;
  const popupConfig = schema.config?.popup;

  const [showPopup, setShowPopup] = useState(false);
  const [confirmedValue, setConfirmedValue] = useState(value);
  const [requestedValue, setRequestedValue] = useState(value);

  useEffect(() => {
    if (value !== confirmedValue) {
      setConfirmedValue(value);
    }
  }, [value]);

  const shouldApplyChange = (_value) => {
    setRequestedValue(_value);
    if (!popupConfig.enabled) return true;

    const requireConfirmation = uiSchema['ui:logic'] === 'requireConfirmation';

    if (_value !== popupConfig.valueToConfirmChangeFrom && requireConfirmation) {
      // we need confirmation to change the value
      setShowPopup(true);
      return false;
    }
    // the value can be changed
    setConfirmedValue(_value);
    return true;
  };

  const onHandleConfirm = () => {
    setConfirmedValue(requestedValue);
    // this post processing has to be triggered from there, as the child component will not trigger it anymore
    if (uiSchema?.['ui:options']?.triggerPostProcess === true || schema?.config?.triggerPostProcess === true) {
      registry.triggerPostProcess(id, requestedValue);
    }
    setShowPopup(false);
  };

  const onHandleCancel = () => {
    setShowPopup(false);
  };

  const updatedProps = {
    ...props,
    value: confirmedValue,
    shouldApplyChange,
  };

  const dialogProps = {
    showPopup,
    onHandleConfirm,
    onHandleCancel,
    popupConfig,
  };

  return (
    <div>
      <FeesRadio {...updatedProps} />
      {showPopup && <ConfirmationDialog {...dialogProps} />}
    </div>
  );
};

export default ConfirmedFeesRadioWidget;
